import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faArrowTrendDown,
  faArrowTrendUp,
  faBell,
  faBullseye,
  faDoorOpen,
  faSparkles,
  faTriangleExclamation,
} from '@fortawesome/pro-duotone-svg-icons'

import {
  QuickPassNotification,
  QuickPassNotificationType,
} from '../../_types/quickpass-notification'
import { formatRelativeTime } from '../../_utilities/relative-time'
import { NotificationAccentTone, NotificationBuckets } from './types'

/**
 * Picks the icon that represents a notification type. Glyphs are
 * intentionally simple so the row stays compact and accessible.
 *
 * @param {QuickPassNotificationType} type - the notification type
 * @returns {IconDefinition} the icon that represents the type
 */
const getNotificationGlyph = (
  type: QuickPassNotificationType,
): IconDefinition => {
  if (type === 'imported-listing-price-dropped') {
    return faArrowTrendDown
  }

  if (type === 'imported-listing-price-increased') {
    return faArrowTrendUp
  }

  if (type === 'imported-listing-became-available') {
    return faSparkles
  }

  if (type === 'imported-listing-became-unavailable') {
    return faDoorOpen
  }

  if (type === 'imported-listing-scrape-failing') {
    return faTriangleExclamation
  }

  if (type === 'preference-match') {
    return faBullseye
  }

  return faBell
}

/**
 * Picks the accent tone variant used for the unread indicator strip on
 * each notification row. The accent maps to a styled-component variant
 * so the colour stays in CSS instead of leaking into inline styles.
 *
 * @param {QuickPassNotificationType} type - the notification type
 * @returns {NotificationAccentTone} the accent tone variant for that type
 */
const getNotificationAccentTone = (
  type: QuickPassNotificationType,
): NotificationAccentTone => {
  if (type === 'imported-listing-price-dropped') {
    return 'success'
  }

  if (type === 'imported-listing-became-available') {
    return 'success'
  }

  if (type === 'imported-listing-price-increased') {
    return 'warmth'
  }

  if (type === 'imported-listing-became-unavailable') {
    return 'warmth'
  }

  if (type === 'imported-listing-scrape-failing') {
    return 'celebration'
  }

  return 'primary'
}

/**
 * Picks the renter-facing subtitle text for the drawer header based on
 * the unread count. Three branches kept flat to avoid nested ternaries.
 *
 * @param {number} unreadCount - the number of unread notifications
 * @returns {string} the subtitle to render under the header title
 */
const buildHeaderSubtitle = (unreadCount: number): string => {
  if (unreadCount === 0) {
    return "You're all caught up."
  }

  if (unreadCount === 1) {
    return '1 unread update'
  }

  return `${unreadCount} unread updates`
}

/**
 * Splits notifications into unread + read buckets so the drawer can render
 * them under separate headers. Each bucket preserves the original
 * newest-first order.
 *
 * @param {QuickPassNotification[]} notifications - the full notification list
 * @returns {{ unread: QuickPassNotification[]; read: QuickPassNotification[] }} the partitioned buckets
 */
const partitionByReadState = (
  notifications: QuickPassNotification[],
): NotificationBuckets => {
  const unread: QuickPassNotification[] = []
  const read: QuickPassNotification[] = []

  for (const entry of notifications) {
    if (entry.isRead === true) {
      read.push(entry)
    } else {
      unread.push(entry)
    }
  }

  return { unread, read }
}

const drawerBackdropMotion = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
}

const drawerPanelMotion = {
  hidden: { x: '100%' },
  visible: { x: 0 },
}

export {
  getNotificationGlyph,
  getNotificationAccentTone,
  buildHeaderSubtitle,
  formatRelativeTime,
  partitionByReadState,
  drawerBackdropMotion,
  drawerPanelMotion,
}
